import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import IconButton from '~/components/buttons/NewIconButton';
import DatePicker from '~/components/inputs/DatePicker/DatePicker';
import SimpleSelect from '~/components/inputs/Select/SimpleSelect';
import AmountField from '~/components/inputs/TextField/AmountField';
import Text from '~/components/typographies/Text';
import IconDelete from '~/icons/icon-delete.svg?react';
import { DocumentBuilderForm } from '~/types/document-builder';
import { FlowPaymentStatus } from '~/types/flow';
import { formatSign } from '~/utils/money';
import styles from './FlowForm.module.scss';

interface Props {
  canDelete: boolean;
  index: number;
  onDelete: () => void;
  currency: string;
  isDisabled?: boolean;
  presetDates: {
    date: Date;
    id: string;
    label: string;
  }[];
}

const FlowForm: React.FC<Props> = ({ canDelete, index, onDelete, currency, isDisabled, presetDates }) => {
  const { t } = useTranslation('documents', { keyPrefix: 'builder' });

  const { control } = useFormContext<DocumentBuilderForm>();
  const currencySymbol = formatSign(currency);

  return (
    <div className={styles.container}>
      <div className={styles.labelContainer}>
        <Text variant="body1" weight="semibold" className={styles.label}>
          {t('labels.flow_number', { number: index + 1 })}
        </Text>
      </div>
      <Controller
        control={control}
        name={`flows.${index}.expectedPaymentDate`}
        render={({ field, fieldState }) => (
          <DatePicker
            {...field}
            className={styles.input}
            errorMessage={fieldState.error?.message}
            label={t('fields.flow_date.label')}
            isInvalid={fieldState.invalid}
            presetDates={presetDates}
            isDisabled={isDisabled}
          />
        )}
      />
      <Controller
        control={control}
        name={`flows.${index}.amount`}
        render={({ field, fieldState }) => (
          <AmountField
            {...field}
            className={styles.input}
            errorMessage={fieldState.error?.message}
            endAddon={currencySymbol}
            label={t('fields.flow_amount.label')}
            isInvalid={fieldState.invalid}
            isDisabled={isDisabled}
          />
        )}
      />
      <Controller
        control={control}
        name={`flows.${index}.paymentStatus`}
        render={({ field, fieldState }) => (
          <SimpleSelect
            {...field}
            className={styles.control}
            errorMessage={fieldState.error?.message}
            isInvalid={fieldState.invalid}
            label={t('fields.flow_payment_status.label')}
            isRequired
            options={[
              {
                value: FlowPaymentStatus.Paid,
                label: t('fields.flow_payment_status.paid'),
              },
              {
                value: FlowPaymentStatus.ToPay,
                label: t('fields.flow_payment_status.to_pay'),
              },
            ]}
            isDisabled={isDisabled}
          />
        )}
      />

      {canDelete && (
        <div className={styles.deleteContainer}>
          <IconButton onClick={() => onDelete()} Icon={IconDelete} variant={'ghost'} />
        </div>
      )}
    </div>
  );
};

export default FlowForm;
